// src/calculators/index.js — екран „Калкулатори" (Фаза 3.2). Пренесен дословно.
// Legacy чек листът вика openCalc/CALC_LABELS по глобални имена — публикуват се долу.

import { el, clear, field, input } from '../shared/dom.js';
import { money } from '../shared/format.js';
import { dateInput, localTodayIso } from '../shared/dates.js';
import { panelH2, searchInput } from '../shared/ui.js';
import { canEditPerm } from '../shared/state.js';
import { buildDividendCalc, dividendToProtocol } from './dividend.js';
// Обезщетенията при прекратяване са в отделен файл (./severance.js);
// нормативната сметка е в services/severance.js — обща за тестове и екран.
import { buildSeveranceCalc } from './severance.js';
import { buildNoticeCalc } from './notice.js';
// Стаж, клас и работни дни (Фаза 3) — сметката е в services/seniority.js.
import { buildSeniorityCalc, buildExperienceBonusCalc, buildWorkdaysCalc } from './seniority.js';
// Отпуск, давност, извънреден труд, имуществена отговорност (Фаза 4) —
// сметката е в services/{leave,overtime,liability}.js.
import { buildAnnualLeaveCalc, buildLeaveExpiryCalc, buildOvertimeCalc, buildLiabilityCalc } from './labor.js';
// Фаза 5 — остатъкът от каталога по КТ, с колона „Кога се използва" +
// цитираните текстове. Сметката е в services/{dismissal,worktime,family}.js.
import {
  buildUnfairDismissalCalc, buildUnregisteredTerminationCalc, buildPayDeadlineCalc,
  buildClaimDeadlinesCalc, buildProbationCalc, buildMaternityCalc,
  buildTargetedLeaveCalc, buildUnpaidLeaveCalc, buildNightWorkCalc,
  buildSirvCalc, buildHolidayWorkCalc, buildSickDaysCalc,
} from './labor2.js';
// „Запази в досие" — при калкулаторите „за лице" (toDossier в CALCS).
import { saveCalcToDossier } from '../dossiers/save.js';
// Правното основание (задача 6/6): колоната withLaw + иконката при полето.
import { withLaw, hintify, ECB_RATE_WARNING } from './law.js';
// Наем по ЗДДФЛ (задача 4/6) — сметката е в services/rent.js.
import { buildRentCalc } from './rent.js';
// Декларацията по чл. 55/чл. 201 (задача 5/6) — сметката е в services/chl55.js.
import { buildChl55Calc } from './chl55.js';
// Граждански договор (ПРОМТ-21) — сметката е в services/civil.js.
import { buildCivilCalc } from './civil.js';
// Промяна на авансовите вноски по чл. 88 ЗКПО (ПРОМТ-22) — services/advanceChange.js.
import { buildAdvanceChangeCalc } from './advanceChange.js';
import { setZkpoChangePrefill } from './prefill.js';

// Един калкулатор: полета отгоре, резултат отдолу с откроен краен ред.
// (Ползва се в новия изглед „списък вляво + голям панел вдясно".)
function calcCard(title, rows, computeFn, note, hint) {
  const inputs = {};
  const result = el('div', { class: 'calc-result' });
  function recompute() {
    const vals = {};
    for (const k in inputs) { const n = inputs[k]; vals[k] = (n.dataset && 'iso' in n.dataset) ? n.dataset.iso : n.value; }
    clear(result);
    (computeFn(vals) || []).forEach((ln) => result.appendChild(
      el('div', { class: 'calc-line' + (ln.big ? ' big' : '') }, el('span', { text: ln.label }), el('span', { text: ln.value }))));
  }
  const body = el('div', { class: 'calc-fields' });
  rows.forEach((r) => {
    let inp;
    if (r.type === 'select') inp = el('select', {}, ...r.options.map((o) => el('option', { value: o.v, text: o.t, selected: String(o.v) === String(r.value) })));
    else if (r.type === 'date') inp = dateInput(r.value || '');
    else inp = input({ type: r.type || 'number', step: r.step || '0.01', value: r.value != null ? r.value : '' });
    inputs[r.key] = inp;
    inp.addEventListener('input', recompute); inp.addEventListener('change', recompute);
    const fEl = field(r.label, inp);
    // Цитат при полето (задача 6): { hint: { ref, text } } в описанието на реда.
    if (r.hint) hintify(fEl, r.hint.ref, r.hint.text);
    body.appendChild(fEl);
  });
  recompute();
  return el('div', { class: 'panel calc-panel' },
    el('h3', { class: 'calc-title', text: title }),
    body,
    hint ? el('div', { class: 'calc-hint-line' }, hint) : null,
    result,
    note ? el('div', { class: 'calc-note' }, note) : null);
}
// Резултат в пари: през money() като цялата система („540,00 €"), не голо
// число с точка (В.6). Само евро — двойното „€ / лв." падна на 01.09.2026
// (решение на Макс; сверено в lex.bg — задължителният период за двойно
// обозначаване изтече на 08.08.2026, чл. 15, ал. 2 ЗВЕРБ).
function _eur(x) { return money(Math.round((Number(x) || 0) * 100) / 100, 'EUR'); }

function calcVat() {
  // Цитатите са сверени дословно в lex.bg (25.08.2026). Бележка: от
  // 01.07.2022 намалената ставка 9 % е в ОТДЕЛЕН чл. 66а, не „чл. 66, ал. 2".
  return withLaw(calcCard('ДДС калкулатор', [
    { key: 'amount', label: 'Сума', value: 100 },
    { key: 'rate', label: 'Ставка ДДС', type: 'select', value: 20, options: [{ v: 20, t: '20%' }, { v: 9, t: '9%' }, { v: 0, t: '0%' }],
      hint: { ref: 'чл. 66, ал. 1 и чл. 66а ЗДДС', text: '20 на сто — стандартна; 9 на сто — настаняване, книги и др. (чл. 66а); нулевата е за изрично посочени доставки.' } },
    { key: 'mode', label: 'Режим', type: 'select', value: 'add', options: [{ v: 'add', t: 'Сумата е БЕЗ ДДС (начисли отгоре)' }, { v: 'incl', t: 'Сумата е С ДДС (извади)' }],
      hint: { ref: 'чл. 67, ал. 2 ЗДДС', text: 'Когато не е посочено, че данъкът се дължи отделно, той се смята включен в цената.' } },
  ], (v) => {
    const amount = Number(v.amount) || 0, rate = Number(v.rate) || 0; let base, vat, total;
    if (v.mode === 'incl') { total = amount; base = total / (1 + rate / 100); vat = total - base; }
    else { base = amount; vat = base * rate / 100; total = base + vat; }
    return [{ label: 'Данъчна основа', value: _eur(base) }, { label: 'ДДС ' + rate + '%', value: _eur(vat) }, { label: 'Сума с ДДС', value: _eur(total), big: true }];
  }), {
    when: [
      'Издавате фактура и трябва да начислите 20 % върху договорената цена без ДДС.',
      'Клиент е платил обща сума „с ДДС" и трябва да извадите основата и данъка за осчетоводяване.',
      'Хотелско настаняване или книги — проверка на сметката с намалената ставка 9 % (чл. 66а).',
      'Бърза сверка на основа/данък преди подаване на дневниците по ЗДДС.',
    ],
    quotes: [
      { ref: 'чл. 66, ал. 1 ЗДДС (изм. ДВ, бр. 52/2022)', text: 'Стандартната ставка на данъка е 20 на сто за облагаемите доставки с място на изпълнение на територията на страната, освен изрично посочените като облагаеми с намалена или нулева ставка на данъка.' },
      { ref: 'чл. 66а ЗДДС (нов — ДВ, бр. 52/2022, изм. ДВ, бр. 102/2022)', text: 'Ставката на данъка е 9 на сто за: 1. доставка на услуга по настаняване, предоставяно в хотели и подобни заведения, включително предоставянето на ваканционно настаняване и отдаване под наем на места за площадки за къмпинг или каравани, с място на изпълнение на територията на страната…' },
      { ref: 'чл. 67, ал. 1 и 2 ЗДДС', text: 'Размерът на данъка се определя, като данъчната основа се умножи по ставката на данъка. Когато при договаряне на доставката не е изрично посочено, че данъкът се дължи отделно, приема се, че той е включен в договорената цена.' },
    ],
  });
}
function calcAdvanceZkpo() {
  const card = calcCard('Авансови вноски по ЗКПО', [
    { key: 'profit', label: 'Прогнозна данъчна печалба', value: 0,
      hint: { ref: 'чл. 83, ал. 1 ЗКПО', text: 'Авансовите вноски се правят на базата на ПРОГНОЗНА данъчна печалба за текущата година.' } },
    { key: 'rate', label: 'Данъчна ставка (%)', value: 10,
      hint: { ref: 'чл. 20 ЗКПО', text: 'Данъчната ставка на корпоративния данък е 10 на сто.' } },
  ], (v) => {
    const p = Number(v.profit) || 0, r = Number(v.rate) || 10; const tax = p * r / 100;
    return [{ label: 'Годишен корпоративен данък', value: _eur(tax), big: true }, { label: 'Месечна авансова вноска', value: _eur(tax / 12) }, { label: 'Тримесечна авансова вноска', value: _eur(tax / 4) }];
  }, 'Месечни аванси — при нетни приходи от продажби над 3 000 000 лв.; тримесечни — при 300 000–3 000 000 лв.; под 300 000 лв. — без авансови вноски (праговете в закона още са в левове; четат се в евро по ЗВЕРБ).');
  // Есенната проверка по чл. 88 (ПРОМТ-22) тръгва от сметнатото тук: годишният
  // данък стига до „Промяна на авансовите вноски" като досегашна вноска.
  card.appendChild(el('div', { class: 'calc-todoc' },
    el('button', { class: 'btn ghost', type: 'button', text: 'Към промяна на вноските (чл. 88)', onClick: () => {
      const [profit, rate] = [...card.querySelectorAll('.calc-fields input')].map((n) => Number(n.value) || 0);
      setZkpoChangePrefill({ annualTax: Math.round(profit * (rate || 10)) / 100 });
      openCalc('zkpo_change');
    } }),
    el('div', { class: 'calc-todoc-hint', text: 'Отваря проверката до 15 ноември с тази вноска като досегашна — въвеждате новата прогноза.' })));
  return withLaw(card, {
    when: [
      'В началото на годината фирмата прогнозира печалбата и определя месечни или тримесечни аванси.',
      'Проверка ДАЛИ фирмата изобщо дължи аванси — под 300 000 лв. нетни приходи (за годината преди предходната) не се правят.',
      'Новоучредено дружество — без аванси за годината на учредяването и следващата.',
      'Преценка за корекция на прогнозата (чл. 88), за да не се стигне до лихва при голямо превишение.',
    ],
    quotes: [
      { ref: 'чл. 20 ЗКПО', text: 'Данъчната ставка на корпоративния данък е 10 на сто.' },
      { ref: 'чл. 83, ал. 1 и 2 ЗКПО (изм. ДВ, бр. 104/2020)', text: 'Данъчно задължените лица правят месечни или тримесечни авансови вноски за корпоративния данък, на базата на прогнозна данъчна печалба за текущата година. Авансови вноски не правят: 1. данъчно задължените лица, чиито нетни приходи от продажби за годината преди предходната година не превишават 300 000 лв.; 2. новоучредените данъчно задължени лица за годината на учредяването им и за следващата година…' },
      { ref: 'чл. 84 ЗКПО (изм. ДВ, бр. 104/2020)', text: 'Месечните авансови вноски се правят от данъчно задължени лица, чиито нетни приходи от продажби за годината преди предходната година превишават 3 000 000 лв.' },
    ],
  });
}
function calcExpenseTax() {
  // Ставката е ИЗБОР, не свободно число: чл. 216 ЗКПО (изм. ДВ 17/2022) дава
  // 10 % за представителните и социалните разходи (чл. 204, ал. 1, т. 1–2),
  // но само 3 % за личното ползване / разходите в натура (т. 4). Сверено в
  // lex.bg — старата бележка тук твърдеше 10 % и за натурата.
  return withLaw(calcCard('Данък върху разходите / лично ползване', [
    { key: 'base', label: 'Данъчна основа (разход)', value: 0,
      hint: { ref: 'чл. 204, ал. 1 ЗКПО', text: 'Облагат се документално обоснованите представителни разходи, социалните разходи в натура и разходите в натура за лично ползване.' } },
    { key: 'rate', label: 'Ставка по чл. 216 ЗКПО', type: 'select', value: 10, options: [
      { v: 10, t: '10 % — представителни и социални разходи (чл. 204, ал. 1, т. 1–2)' },
      { v: 3, t: '3 % — лично ползване / разходи в натура (чл. 204, ал. 1, т. 4)' },
    ], hint: { ref: 'чл. 216 ЗКПО (изм. ДВ, бр. 17/2022)', text: '10 на сто за разходите по т. 1 и 2; 3 на сто за разходите в натура по т. 4 (личното ползване).' } },
  ], (v) => {
    const b = Number(v.base) || 0, r = Number(v.rate) || 10;
    return [{ label: 'Данък ' + r + '%', value: _eur(b * r / 100), big: true }];
  }, 'Представителните и социалните разходи се облагат с 10 %, а личното ползване (разходите в натура) — с 3 % (чл. 216 ЗКПО, изм. ДВ, бр. 17 от 2022 г.). Данъкът е годишен и се декларира с ГДД по чл. 92.'), {
    when: [
      'Фирмата има представителни разходи (обяди с клиенти, посрещане) и начислява 10 % върху тях.',
      'Социални придобивки в натура за персонала (вкл. ваучери за храна на електронен носител над освободения размер).',
      'Служебна кола или имот се ползва и лично — данък 3 % върху разходите в натура (чл. 204, ал. 1, т. 4).',
      'Преценка дали е по-изгодно личното ползване да се обложи като непарична придобивка по ЗДДФЛ вместо с данъка по чл. 204, ал. 2.',
    ],
    quotes: [
      { ref: 'чл. 204, ал. 1 ЗКПО (изм. ДВ, бр. 75/2016)', text: 'С данък върху разходите се облагат следните документално обосновани разходи: 1. представителните разходи, свързани с дейността; 2. социалните разходи, предоставени в натура на работници и служители и лица, наети по договор за управление и контрол (наети лица)… 4. разходите в натура, свързани със собствени, наети и/или предоставени за ползване активи, предоставени за лично ползване…' },
      { ref: 'чл. 216 ЗКПО (изм. ДВ, бр. 17/2022)', text: 'Данъчната ставка на данъка върху разходите по чл. 204, ал. 1, т. 1 и 2 е 10 на сто. Данъчната ставка на данъка върху разходите по чл. 204, ал. 1, т. 4 е 3 на сто.' },
    ],
  });
}
// Дивидентният калкулатор е в отделен файл (./dividend.js): двупосочен,
// по фирма, с ръчно разпределение по собственици.
function calcInterest() {
  // От 01.01.2026 г. формулата е СМЕНЕНА (ПМС № 426/2014, изм. ДВ, бр. 115
  // от 2025 г., сверено в lex.bg): лихвеният процент по основните операции
  // по рефинансиране на ЕЦБ (в сила на 1 януари / 1 юли) + 8 процентни
  // пункта. Старата „ОЛП на БНБ + 10" важи само за периоди до 31.12.2025 г.
  const today = localTodayIso();
  return withLaw(calcCard('Законна лихва за забава', [
    { key: 'principal', label: 'Главница', value: 0,
      hint: { ref: 'чл. 86 ЗЗД', text: 'При неизпълнение на парично задължение длъжникът дължи обезщетение в размер на законната лихва от деня на забавата.' } },
    { key: 'annual', label: 'Годишен лихвен % (ЕЦБ + 8 пункта)', value: 8,
      hint: { ref: 'ПМС № 426/2014, чл. единствен (изм. ДВ, бр. 115/2025, от 01.01.2026)', text: 'Лихвеният процент по основните операции по рефинансиране на ЕЦБ (към 1 януари / 1 юли) + 8 процентни пункта.' } },
    { key: 'from', label: 'От дата', type: 'date', value: today },
    { key: 'to', label: 'До дата', type: 'date', value: today },
    { key: 'basis', label: 'База дни/година', type: 'select', value: 360, options: [{ v: 360, t: '360 дни (ПМС 426 — дневната лихва е 1/360)' }, { v: 365, t: '365 дни (ако е уговорено в договора)' }] },
  ], (v) => {
    const p = Number(v.principal) || 0, a = Number(v.annual) || 0, basis = Number(v.basis) || 360;
    let days = 0; if (v.from && v.to) days = Math.max(0, Math.round((new Date(v.to) - new Date(v.from)) / 86400000));
    const interest = p * (a / 100) * days / basis;
    return [{ label: 'Брой дни', value: String(days) }, { label: 'Лихва', value: _eur(interest), big: true }, { label: 'Главница + лихва', value: _eur(p + interest) }];
  }, 'От 01.01.2026 г.: годишният процент = лихвеният процент по основните операции по рефинансиране на ЕЦБ (към 1 януари, съответно 1 юли) + 8 процентни пункта; дневният размер е 1/360 от годишния (ПМС № 426/2014, изм. ДВ 115/2025). За периоди до 31.12.2025 г. важи старата формула: ОЛП на БНБ + 10 пункта.',
  // Видим ред, не бележка под резултата: оставеното „8" на практика значи
  // ЕЦБ = 0 — тиха грешка, която подценява лихвата (В.6).
  ECB_RATE_WARNING), {
    when: [
      'Клиент бави фактура — смятате лихвата за забава към поканата за плащане.',
      'Съставяте покана за доброволно изпълнение или заявление по чл. 410 ГПК с изчислена лихва.',
      'Между търговци лихва се дължи и без уговорка (чл. 294 ТЗ) — проверка преди спор.',
      'Периоди преди и след 01.01.2026 г. се смятат с РАЗЛИЧНА формула — при дълга забава разделете периода.',
    ],
    quotes: [
      { ref: 'чл. 86 ЗЗД', text: 'При неизпълнение на парично задължение длъжникът дължи обезщетение в размер на законната лихва от деня на забавата. За действително претърпени вреди в по-висок размер кредиторът може да иска обезщетение съобразно общите правила. Размерът на законната лихва се определя от Министерския съвет.' },
      { ref: 'ПМС № 426/2014, чл. единствен, ал. 1 и 2 (изм. ДВ, бр. 115/2025, в сила от 01.01.2026)', text: 'Определя годишния размер на законната лихва за просрочени парични задължения в размер на лихвения процент по основните операции по рефинансиране на Европейската централна банка, в сила на 1 януари, съответно на 1 юли, на текущата година… плюс 8 процентни пункта. Дневният размер на законната лихва за просрочени парични задължения е равен на 1/360 част от годишния размер.' },
      { ref: 'чл. 294, ал. 1 и 2 ТЗ', text: 'Между търговци лихва се дължи, освен ако е уговорено друго. Лихва върху лихва се дължи само ако е уговорена.' },
    ],
  });
}

// Регистърът на калкулаторите: менюто по категории идва оттук; build() строи
// панела. Калкулатор може да декларира и toDocument — действие
// „към документ": { label, hint?, run(panel) }. Регистърът рисува бутона
// под панела; run() получава панела и решава какво да отвори.
// cat = категория в лентата (виж CATEGORIES); group = подгрупа в „Труд и
// осигуряване". Категориите са решение на собственика (задача 2/6).
const CALCS = [
  { key: 'vat', cat: 'ddc', label: 'ДДС', desc: 'Начисли или извади ДДС', build: calcVat },
  { key: 'zkpo_advance', cat: 'zkpo', label: 'Авансови ЗКПО', desc: 'Месечни/тримесечни вноски', build: calcAdvanceZkpo },
  { key: 'zkpo_change', cat: 'zkpo', label: 'Промяна на авансовите вноски', desc: 'чл. 88 ЗКПО — колко да е новата вноска, за да няма лихва', build: buildAdvanceChangeCalc },
  { key: 'expense', cat: 'zkpo', label: 'Данък разходи', desc: 'Представителни, лично ползване', build: calcExpenseTax },
  {
    key: 'dividend', cat: 'zddfl', label: 'Дивидент', desc: 'Данък 5%, разпределение по собственици', build: buildDividendCalc,
    toDocument: {
      label: 'Създай дивидентен протокол',
      hint: 'Отваря протокола предварително попълнен — преглеждаш и чак тогава запазваш.',
      run: dividendToProtocol,
    },
  },
  { key: 'rent', cat: 'zddfl', toDossier: true, label: 'Наем', desc: 'чл. 31 и чл. 44 ЗДДФЛ — авансов данък', build: buildRentCalc },
  // До „Наем": там хората търсят „плащане на физическо лице" (ПРОМТ-21).
  { key: 'civil', cat: 'zddfl', toDossier: true, label: 'Граждански договор', desc: 'осигуровки, данък и нето по ГД', build: buildCivilCalc },
  { key: 'chl55', cat: 'deklaracii', toDossier: true, label: 'чл. 55', desc: 'ЗДДФЛ чл. 55, ал. 1 и ЗКПО чл. 201, ал. 1', build: buildChl55Calc },
  { key: 'interest', cat: 'targovsko', label: 'Законна лихва', desc: 'Лихва за забава по дни', build: calcInterest },
  // toDossier: true = калкулатор „ЗА ЛИЦЕ" — има бутон „Запази в досие"
  // (src/dossiers/save.js). Фирмените сметки (ДДС, ЗКПО, дивидент, лихва)
  // нарочно го НЯМАТ — те не са за конкретен човек.
  { key: 'severance', cat: 'trud', group: 'prekratyavane', toDossier: true, label: 'Обезщетения при напускане', desc: 'чл. 224, 220 и 222 КТ', build: buildSeveranceCalc },
  { key: 'seniority', cat: 'trud', group: 'stazh', toDossier: true, label: 'Трудов стаж', desc: 'чл. 355 КТ — сумиране на периоди', build: buildSeniorityCalc },
  { key: 'experience-bonus', cat: 'trud', group: 'stazh', toDossier: true, label: 'Клас прослужено време', desc: 'НСОРЗ чл. 12 — мин. 0,6%/год.', build: buildExperienceBonusCalc },
  { key: 'workdays', cat: 'drugi', toDossier: true, label: 'Работни дни', desc: 'работни/календарни дни между две дати', build: buildWorkdaysCalc },
  { key: 'annual-leave', cat: 'trud', group: 'otpuski', toDossier: true, label: 'Полагаем отпуск', desc: 'чл. 155–156а КТ — пропорция по месеци', build: buildAnnualLeaveCalc },
  { key: 'leave-expiry', cat: 'trud', group: 'otpuski', toDossier: true, label: 'Давност на отпуска', desc: 'чл. 176а КТ — кой отпуск кога изгаря', build: buildLeaveExpiryCalc },
  { key: 'overtime', cat: 'trud', group: 'rabotno-vreme', toDossier: true, label: 'Извънреден труд', desc: 'чл. 262 КТ — 50/75/100% и лимити', build: buildOvertimeCalc },
  { key: 'liability', cat: 'trud', group: 'stazh', toDossier: true, label: 'Имуществена отговорност', desc: 'чл. 206, 210 КТ + удръжки по чл. 446 ГПК', build: buildLiabilityCalc },
  { key: 'notice', cat: 'trud', group: 'prekratyavane', toDossier: true, label: 'Предизвестие и прекратяване', desc: 'чл. 326, 220, 221, 331 КТ', build: buildNoticeCalc },
  // — Фаза 5: прекратяване и срокове —
  { key: 'unfair-dismissal', cat: 'trud', group: 'prekratyavane', toDossier: true, label: 'Незаконно уволнение', desc: 'чл. 225 КТ — до 6 месеца БТВ', build: buildUnfairDismissalCalc },
  { key: 'unregistered-termination', cat: 'trud', group: 'prekratyavane', toDossier: true, label: 'Невписано прекратяване', desc: 'чл. 226 КТ — забавени документи/вписване', build: buildUnregisteredTerminationCalc },
  { key: 'pay-deadline', cat: 'trud', group: 'prekratyavane', toDossier: true, label: 'Срок за обезщетенията', desc: 'чл. 228, ал. 3 КТ — докога се плащат', build: buildPayDeadlineCalc },
  { key: 'claim-deadlines', cat: 'trud', group: 'prekratyavane', toDossier: true, label: 'Давност по спорове', desc: 'чл. 358 КТ — 1 м. / 2 м. / 3 г.', build: buildClaimDeadlinesCalc },
  { key: 'probation', cat: 'trud', group: 'prekratyavane', toDossier: true, label: 'Срок за изпитване', desc: 'чл. 70–71 КТ — кога изтича', build: buildProbationCalc },
  // — семейство и отпуски —
  { key: 'maternity', cat: 'trud', group: 'otpuski', toDossier: true, label: 'Майчинство и бащинство', desc: 'чл. 163–164в КТ — всички срокове', build: buildMaternityCalc },
  { key: 'targeted-leave', cat: 'trud', group: 'otpuski', toDossier: true, label: 'Целеви отпуски', desc: 'чл. 157 КТ — брак, кръводаряване, смърт', build: buildTargetedLeaveCalc },
  { key: 'unpaid-leave', cat: 'trud', group: 'otpuski', toDossier: true, label: 'Неплатен отпуск', desc: 'чл. 160 КТ — колко е стаж', build: buildUnpaidLeaveCalc },
  // — работно време —
  { key: 'night-work', cat: 'trud', group: 'rabotno-vreme', toDossier: true, label: 'Нощен труд', desc: 'чл. 261 КТ — добавка на нощен час', build: buildNightWorkCalc },
  { key: 'sirv', cat: 'trud', group: 'rabotno-vreme', toDossier: true, label: 'СИРВ', desc: 'чл. 142 КТ — норма и извънредни', build: buildSirvCalc },
  { key: 'holiday-work', cat: 'trud', group: 'rabotno-vreme', toDossier: true, label: 'Работа на празник', desc: 'чл. 264 КТ — поне двойно', build: buildHolidayWorkCalc },
  { key: 'sick-days', cat: 'trud', group: 'rabotno-vreme', toDossier: true, label: 'Болничен — първите дни', desc: 'чл. 40, ал. 5 КСО — 2 дни от работодателя', build: buildSickDaysCalc },
];

// Ключови думи за търсачката (ПРОМТ-37): с какви думи хората ТЪРСЯТ сметката,
// а не как се казва в закона — „предизвестие", „болничен", „ГД". Всеки
// калкулатор трябва да има поне една (пази го test/calc-search.test.js).
const KEYWORDS = {
  vat: ['ддс', 'данък добавена стойност', 'начисляване', 'с ддс', 'без ддс', '20%', '9%', 'фактура'],
  zkpo_advance: ['авансови вноски', 'аванс', 'корпоративен данък', 'печалба', 'зкпо', 'месечни', 'тримесечни'],
  zkpo_change: ['авансови вноски', 'промяна', 'корекция', 'лихва', 'чл. 88', 'прогноза', '15 ноември'],
  expense: ['данък върху разходите', 'представителни', 'лично ползване', 'служебна кола', 'социални разходи', 'ваучери'],
  dividend: ['дивидент', 'печалба', 'съдружници', 'собственици', 'разпределение', '5%', 'протокол'],
  rent: ['наем', 'наемодател', 'физическо лице', 'авансов данък', 'имот', 'зддфл'],
  civil: ['граждански договор', 'гд', 'хонорар', 'физическо лице', 'осигуровки', 'нето', 'бруто', 'извън трудово'],
  chl55: ['декларация', 'чл. 55', 'чл. 201', 'удържан данък', 'нерезидент', 'тримесечие'],
  interest: ['законна лихва', 'лихва', 'забава', 'просрочие', 'неплатена фактура', 'основен лихвен процент'],
  severance: ['обезщетение', 'напускане', 'пенсия', 'съкращение', 'неизползван отпуск', 'уволнение', 'брутна заплата'],
  seniority: ['стаж', 'трудов стаж', 'периоди', 'години', 'месеци', 'дни'],
  'experience-bonus': ['клас', 'прослужено време', 'стаж', 'добавка', '0,6%', 'надбавка'],
  workdays: ['работни дни', 'календарни дни', 'празници', 'почивни дни', 'брой дни'],
  'annual-leave': ['отпуск', 'платен отпуск', 'полагаем', '20 дни', 'пропорционално', 'нов служител'],
  'leave-expiry': ['отпуск', 'давност', 'изгаря', 'стар отпуск', 'неизползван отпуск'],
  overtime: ['извънреден труд', 'извънредни часове', '50%', '75%', '100%', 'лимит'],
  liability: ['имуществена отговорност', 'щета', 'удръжка', 'липса', 'запор'],
  notice: ['предизвестие', 'напускане', 'прекратяване', 'уволнение', '30 дни', 'обезщетение за предизвестие'],
  'unfair-dismissal': ['незаконно уволнение', 'уволнение', 'съд', 'обезщетение', 'без работа'],
  'unregistered-termination': ['трудова книжка', 'прекратяване', 'невписано', 'забавени документи'],
  'pay-deadline': ['срок', 'обезщетения', 'плащане', 'последна заплата', 'докога'],
  'claim-deadlines': ['давност', 'спор', 'иск', 'съд', 'срок'],
  probation: ['изпитване', 'срок за изпитване', 'изпитателен срок', 'пробен период', '6 месеца'],
  maternity: ['майчинство', 'бащинство', 'бременност', 'раждане', 'дете', 'отпуск', 'гледане на дете'],
  'targeted-leave': ['отпуск', 'сватба', 'брак', 'смърт', 'кръводаряване', 'целеви'],
  'unpaid-leave': ['неплатен отпуск', 'отпуск', 'стаж', '30 дни'],
  'night-work': ['нощен труд', 'нощни часове', 'нощна смяна', 'добавка'],
  sirv: ['сирв', 'сумирано изчисляване', 'работно време', 'смени', 'норма'],
  'holiday-work': ['празник', 'официален празник', 'двойно', 'работа на празник'],
  'sick-days': ['болничен', 'болнични', 'временна нетрудоспособност', 'първите дни', 'работодател', 'нои'],
};
CALCS.forEach((c) => { c.keywords = KEYWORDS[c.key] || []; });

// Търсене по име, описание и ключови думи. Всяка дума от заявката трябва да
// се намери някъде — „отпуск дете" дава майчинството, не всички отпуски.
export function findCalcs(q) {
  const words = String(q || '').toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  return CALCS.filter((c) => {
    const hay = [c.label, c.desc, ...(c.keywords || [])].join(' | ').toLowerCase();
    return words.every((w) => hay.includes(w));
  });
}

// Категориите на лентата — РЕДЪТ ТУК Е РЕДЪТ НА ЕКРАНА. Категория без нито
// един наличен калкулатор не се рисува: 'rent' (ЗДДФЛ, задача 4) и 'chl55'
// (Декларации, задача 5) са предвидени в схемата, но ключовете им още ги
// няма в CALCS — менюто просто ги подминава, регистърът не се пипа пак.
const CATEGORIES = [
  { cat: 'ddc', label: 'ЗДДС' },
  { cat: 'zkpo', label: 'ЗКПО' },
  { cat: 'zddfl', label: 'ЗДДФЛ' },
  {
    cat: 'trud', label: 'Труд и осигуряване',
    groups: [
      { group: 'prekratyavane', label: 'Прекратяване и обезщетения' },
      { group: 'otpuski', label: 'Отпуски и семейство' },
      { group: 'rabotno-vreme', label: 'Работно време' },
      { group: 'stazh', label: 'Стаж и възнаграждения' },
    ],
  },
  { cat: 'targovsko', label: 'Търговско и ДОПК' },
  { cat: 'deklaracii', label: 'Декларации' },
  { cat: 'drugi', label: 'Други' },
];

// Регистърът се изнася и за обхватния тест на цитатите (calculators-law):
// той обхожда ВСИЧКИ калкулатори и не позволява нов без правно основание.
export { CALCS };
export const CALC_LABELS = Object.fromEntries(CALCS.map((c) => [c.key, c.label]));
let _calcTarget = '';
let _active = 'vat'; // избраният калкулатор се помни между рендерите
export function openCalc(key) { _calcTarget = key; window.CRM.state.view = 'calculators'; window.CRM.renderShell(); }

export function view(page) {
  clear(page);
  // Дълбок линк (чек листът вика openCalc): менюто после само разгръща
  // категорията на избрания калкулатор.
  const fromDeepLink = !!_calcTarget;
  if (_calcTarget) { if (CALCS.some((c) => c.key === _calcTarget)) _active = _calcTarget; _calcTarget = ''; }
  if (!CALCS.some((c) => c.key === _active)) _active = CALCS[0].key;

  page.appendChild(el('div', { class: 'panel-head' }, ...panelH2('Калкулатори'),
    el('span', { style: 'color:var(--muted);font-size:13px;margin-left:auto', text: 'Помощни сметки по чек листа за приключване' })));

  const menu = el('nav', { class: 'calc-menu', 'aria-label': 'Категории калкулатори' });
  const main = el('div', { class: 'calc-main' });
  const cats = []; // { def, keys, wrap, btn, list }
  let openEntry = null; // отвореният падащ списък (най-много един)

  function closeOpen(focusBtn) {
    if (!openEntry) return;
    openEntry.list.style.display = 'none';
    openEntry.btn.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', onDocClick);
    const b = openEntry.btn;
    openEntry = null;
    if (focusBtn) b.focus();
  }
  function onDocClick(e) { if (openEntry && !openEntry.wrap.contains(e.target)) closeOpen(false); }
  function openList(entry, focusFirst) {
    if (openEntry !== entry) {
      closeOpen(false);
      entry.list.style.display = '';
      entry.btn.setAttribute('aria-expanded', 'true');
      openEntry = entry;
      // Клик извън списъка го затваря (изчакваме текущия клик да отшуми —
      // същият модел като actionsMenu в shared/ui.js).
      setTimeout(() => document.addEventListener('click', onDocClick), 0);
    }
    if (focusFirst) { const f = entry.list.querySelector('.calc-item'); if (f) f.focus(); }
  }

  // select(key) е същият като досега — сменя само панела; менюто отгоре
  // единствено отразява кой е активен.
  function select(key) {
    _active = key;
    cats.forEach((entry) => {
      entry.btn.classList.toggle('active', entry.keys.includes(key));
      [...entry.list.querySelectorAll('.calc-item')].forEach((b) => b.classList.toggle('active', b.dataset.key === key));
    });
    clear(main);
    const def = CALCS.find((c) => c.key === key);
    const panel = def.build();
    main.appendChild(panel);
    // Бутоните „към документ"/„в досие" се закачат ВЪТРЕ в панела, точно
    // под резултата — под цялата страница (след правната колона) те се
    // губеха от погледа (забележка на Макс, 26.08.2026). При обвивка
    // .calc-with-law панелът е първото дете с .calc-panel.
    const btnHome = panel.querySelector && panel.querySelector('.calc-panel') || panel;
    // Бутоните за запис се виждаха и без право — отказът идваше чак при
    // записа (грешка от картата, 16.10.2026). Сега: дивидентният протокол —
    // с право „Протоколи", „Запази в досие" — с право „Досиета" (за промяна).
    if (def.toDocument && canEditPerm('protocols')) {
      btnHome.appendChild(el('div', { class: 'calc-todoc' },
        el('button', { class: 'btn', type: 'button', text: def.toDocument.label, onClick: () => def.toDocument.run(panel) }),
        def.toDocument.hint ? el('div', { class: 'calc-todoc-hint', text: def.toDocument.hint }) : null));
    }
    if (def.toDossier && canEditPerm('dossiers')) {
      btnHome.appendChild(el('div', { class: 'calc-todoc' },
        el('button', { class: 'btn ghost', type: 'button', text: 'Запази в досие', onClick: () => saveCalcToDossier(def, panel) }),
        el('div', { class: 'calc-todoc-hint', text: 'Записва сметката към лице в раздел „Досиета" — с входните данни, резултата и правното основание.' })));
    }
  }

  function calcItem(c) {
    return el('button', {
      type: 'button', class: 'calc-item' + (c.key === _active ? ' active' : ''), 'data-key': c.key,
      // Повторен клик върху активния не преизгражда панела (би изтрил
      // въведеното) — само затваря списъка.
      onClick: () => { if (c.key !== _active) select(c.key); closeOpen(false); },
    },
      el('span', { class: 'calc-item-label', text: c.label }),
      el('span', { class: 'calc-item-desc', text: c.desc }));
  }

  CATEGORIES.forEach((def) => {
    const inCat = CALCS.filter((c) => c.cat === def.cat);
    if (!inCat.length) return; // категория без наличен калкулатор не се рисува
    const listId = 'calc-cat-list-' + def.cat;
    const btn = el('button', {
      type: 'button', class: 'calc-cat-btn',
      'aria-expanded': 'false', 'aria-controls': listId, 'aria-haspopup': 'true',
    }, def.label, el('span', { 'aria-hidden': 'true', text: ' ▾' }));
    const list = el('div', { class: 'menu calc-cat-list', id: listId, style: 'display:none' });
    if (def.groups) {
      // „Труд и осигуряване": списъкът е разделен на подгрупи с ВИДИМИ
      // заглавия, обявени и за екранния четец (role=group + aria-label).
      def.groups.forEach((g) => {
        const own = inCat.filter((c) => c.group === g.group);
        if (!own.length) return;
        list.appendChild(el('div', { class: 'calc-group', role: 'group', 'aria-label': g.label },
          el('h4', { text: g.label }),
          ...own.map(calcItem)));
      });
    } else {
      inCat.forEach((c) => list.appendChild(calcItem(c)));
    }
    const wrap = el('div', { class: 'calc-cat' }, btn, list);
    const entry = { def, keys: inCat.map((c) => c.key), wrap, btn, list };
    btn.addEventListener('click', () => (openEntry === entry ? closeOpen(false) : openList(entry, false)));
    btn.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); openList(entry, true); }
    });
    list.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { e.preventDefault(); closeOpen(true); return; }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return; // Tab излиза сам
      e.preventDefault();
      const items = [...list.querySelectorAll('.calc-item')];
      const idx = items.indexOf(document.activeElement);
      const next = e.key === 'ArrowDown'
        ? items[Math.min(items.length - 1, idx + 1)]
        : items[Math.max(0, idx - 1)];
      if (next) next.focus();
    });
    // Излезе ли фокусът от категорията (напр. с Tab), списъкът се затваря —
    // отложено, за да не изчезне преди клика върху негов елемент.
    wrap.addEventListener('focusout', () => {
      setTimeout(() => { if (openEntry === entry && !wrap.contains(document.activeElement)) closeOpen(false); }, 0);
    });
    cats.push(entry);
    menu.appendChild(wrap);
  });

  // Търсачката (ПРОМТ-37): над 30 калкулатора в 7 категории — който не знае
  // в коя е „Предизвестие", пише думата. Изборът от резултат е същият select().
  const hits = el('div', { class: 'calc-hits', role: 'list', 'aria-label': 'Намерени калкулатори' });
  const noHit = el('p', { class: 'calc-nohit', role: 'status' });
  const drawHits = (q) => {
    clear(hits); noHit.textContent = '';
    if (!q) return;
    const found = findCalcs(q);
    if (!found.length) { noHit.textContent = 'Няма такъв калкулатор. Опитайте с друга дума, напр. „отпуск“.'; return; }
    found.forEach((c) => {
      const b = calcItem(c);
      b.setAttribute('role', 'listitem');
      // Изборът от резултата чисти търсачката — панелът отдолу е отворен.
      b.addEventListener('click', () => { search.inputEl.value = ''; search.classList.remove('has-value'); drawHits(''); if (c.key !== _active) select(c.key); });
      hits.appendChild(b);
    });
  };
  const search = searchInput({ placeholder: 'Търси калкулатор…', ariaLabel: 'Търси калкулатор по име или дума, напр. отпуск', onSearch: drawHits, delay: 120 });
  page.appendChild(el('div', { class: 'calc-search' }, search));
  page.appendChild(hits);
  page.appendChild(noHit);
  page.appendChild(menu);
  page.appendChild(main);
  select(_active);
  // Дълбокият линк разгръща категорията на отворения калкулатор, за да се
  // вижда КЪДЕ живее той в новата подредба.
  if (fromDeepLink) {
    const entry = cats.find((x) => x.keys.includes(_active));
    if (entry) openList(entry, false);
  }
}

// Legacy мостове за немигрирания чек лист:
window.openCalc = openCalc;
window.CALC_LABELS = CALC_LABELS;
